import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '@/redux/store';
import { useGetDashboardConfigQuery } from '@/services/dashboardConfigApi';
import useDocumentTitle from '@/components/ui/common/document-title';
import { Loader2, Users, Building2, Activity } from 'lucide-react';
import NotFoundPage from '@/components/page-not-found';
import FinancialSection from './FinancialSection';
import MembershipSection from './MembershipSection';
import FacilityCoachSection from './FacilityCoachSection';
import OperationsSection from './OperationsSection';
import { cn } from '@/lib/utils';

const ReceiptIcon = ({ className }: { className?: string }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.75"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    aria-hidden="true"
  >
    <path d="M13 16H8" />
    <path d="M14 8H8" />
    <path d="M16 12H8" />
    <path d="M4 3a1 1 0 0 1 1-1 1.3 1.3 0 0 1 .7.2l.933.6a1.3 1.3 0 0 0 1.4 0l.934-.6a1.3 1.3 0 0 1 1.4 0l.933.6a1.3 1.3 0 0 0 1.4 0l.933-.6a1.3 1.3 0 0 1 1.4 0l.934.6a1.3 1.3 0 0 0 1.4 0l.933-.6A1.3 1.3 0 0 1 19 2a1 1 0 0 1 1 1v18a1 1 0 0 1-1 1 1.3 1.3 0 0 1-.7-.2l-.933-.6a1.3 1.3 0 0 0-1.4 0l-.934.6a1.3 1.3 0 0 1-1.4 0l-.933-.6a1.3 1.3 0 0 0-1.4 0l.933.6a1.3 1.3 0 0 1-1.4 0l-.934-.6a1.3 1.3 0 0 0-1.4 0l-.933.6a1.3 1.3 0 0 1-.7.2 1 1 0 0 1-1-1z" />
  </svg>
);

export default function DashboardConfig() {
  useDocumentTitle('Dashboard configuration | FitnFi admin');

  const orgId = useSelector((state: RootState) => state.auth.userInfo?.user?.org_id) || 0;
  const [activeTab, setActiveTab] = useState<'financial' | 'membership' | 'facility-coach' | 'operations'>('financial');

  const dashboardConfigAccess = (() => {
    try {
      const accessLevels = JSON.parse(localStorage.getItem('accessLevels') || '{}');
      return accessLevels['dashboard-config'] ?? accessLevels.dashboard_config;
    } catch {
      return undefined;
    }
  })();

  const hasNoAccess = dashboardConfigAccess === 'no_access';

  const { isLoading, refetch } = useGetDashboardConfigQuery(
    { orgId },
    { skip: hasNoAccess }
  );

  if (hasNoAccess) {
    return <NotFoundPage />;
  }

  const tabs = [
    { id: 'financial', label: 'Financial', icon: ReceiptIcon },
    { id: 'membership', label: 'Membership', icon: Users },
    { id: 'facility-coach', label: 'Facility and coach', icon: Building2 },
    { id: 'operations', label: 'Operations', icon: Activity },
  ] as const;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <nav aria-label="Settings sections" className="-mx-4 overflow-x-hidden border-b border-border px-4 sm:mx-0 sm:px-0">
          <div className="flex min-w-max gap-6">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveTab(tab.id)}
                  className={cn(
                    '-mb-px flex items-center gap-2 border-b-2 pb-3 text-[13px] font-medium transition-colors',
                    isActive
                      ? 'border-primary text-foreground'
                      : 'border-transparent text-muted-foreground hover:text-foreground'
                  )}
                >
                  <Icon className="h-4 w-4 shrink-0" />
                  {tab.label}
                </button>
              );
            })}
          </div>
        </nav>

        <div className="mt-6">
          {activeTab === 'financial' && <FinancialSection refetch={refetch} />}
          {activeTab === 'membership' && <MembershipSection refetch={refetch} />}
          {activeTab === 'facility-coach' && <FacilityCoachSection refetch={refetch} />}
          {activeTab === 'operations' && <OperationsSection refetch={refetch} />}
        </div>
      </div>
    </div>
  );
}



